import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { simulateRules, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { FlaskConical, Loader2, ArrowUpRight } from "lucide-react";

const Blocked = ({ label, n, testId }) => (
  <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className="font-mono text-sm font-semibold" data-testid={testId}>{n}</div>
  </div>
);

export const RuleSimulator = ({ minConfidence, maxAmount, enabled }) => {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      simulateRules(minConfidence, Number(maxAmount) || 0)
        .then(setRes)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [minConfidence, maxAmount]);

  return (
    <div className={cn("rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4", !enabled && "opacity-60")} data-testid="rule-simulator">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FlaskConical className="h-4 w-4 text-primary" /> What would these rules have done?
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <span className="text-[11px] text-muted-foreground">Replays every past invoice · updates as you drag</span>
      </div>

      {!res ? (
        <div className="h-16" />
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-mono text-3xl font-bold" data-testid="sim-count">{res.would_auto_approve}</span>
            <span className="text-sm text-muted-foreground">
              of <span className="font-mono text-foreground">{res.total_invoices}</span> past invoices would auto-approve
            </span>
            <span className="text-sm text-muted-foreground">·</span>
            <span className="font-mono text-sm font-semibold" data-testid="sim-value">{money(res.would_auto_approve_value)}</span>
            <span className="ml-auto rounded-md bg-primary/15 px-2 py-0.5 font-mono text-xs text-primary" data-testid="sim-rate">{res.rate}%</span>
          </div>
          {!enabled && <p className="text-xs text-amber-500 mt-1">Auto-approval is switched off — these numbers apply once you turn it on.</p>}

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Blocked label="Held: below confidence" n={res.blocked.low_confidence} testId="sim-blocked-confidence" />
            <Blocked label="Held: over amount limit" n={res.blocked.over_limit} testId="sim-blocked-limit" />
            <Blocked label="Held: flagged <80%" n={res.blocked.flagged} testId="sim-blocked-flagged" />
            <Blocked label="Held: possible duplicate" n={res.blocked.duplicate} testId="sim-blocked-duplicate" />
          </div>

          {res.invoices.length > 0 && (
            <ul className="mt-3 max-h-40 overflow-auto d20-scroll divide-y divide-border rounded-lg border border-border bg-card" data-testid="sim-list">
              {res.invoices.map((i) => (
                <li key={i.id}>
                  <button data-testid={`sim-row-${i.id}`} onClick={() => navigate(`/invoice/${i.id}`)} className="w-full flex items-center justify-between gap-3 px-3 py-1.5 text-xs hover:bg-muted/40">
                    <span className="truncate"><span className="font-medium">{i.supplier}</span> <span className="text-muted-foreground font-mono">{i.invoice_number}</span></span>
                    <span className="flex items-center gap-2 shrink-0 font-mono">
                      <span className="text-muted-foreground">{Math.round(i.min_confidence * 100)}%</span>
                      {money(i.total, i.currency)}
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
